/**
 * One GET, with the four states every page in this app has to render:
 * loading, loaded, refreshing-with-stale-data, and failed-with-a-retry.
 *
 * `data` and `error` are held together rather than swapped, so a failed
 * auto-refresh keeps the last good fleet state on screen with a banner over it
 * instead of blanking a dashboard someone is mid-incident with.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import { apiFetch, type ApiFetchOptions } from './api';

export interface ResourceState<T> {
  data: T | null;
  error: unknown;
  /** No data yet and a request is in flight: the only spinner case. */
  loading: boolean;
  /** A request is in flight while data is already on screen. */
  refreshing: boolean;
  /** When `data` last arrived, for "checked N seconds ago". */
  fetchedAt: Date | null;
  reload: () => void;
}

export interface UseResourceOptions extends ApiFetchOptions {
  /** Poll interval in milliseconds. Omit for a one-shot fetch. */
  refreshMs?: number;
  /** Skip the request entirely (e.g. a route param is not known yet). */
  enabled?: boolean;
}

export function useResource<T>(
  path: string,
  options: UseResourceOptions = {},
): ResourceState<T> {
  const { refreshMs, enabled = true, anonymous } = options;

  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [inFlight, setInFlight] = useState<boolean>(enabled);
  const [fetchedAt, setFetchedAt] = useState<Date | null>(null);

  // Bumped by `reload`; also part of the effect key so a manual retry and a
  // path change go down the same code path.
  const [attempt, setAttempt] = useState(0);
  const reload = useCallback(() => setAttempt((n) => n + 1), []);

  const hasData = data !== null;
  const hasDataRef = useRef(hasData);
  hasDataRef.current = hasData;

  useEffect(() => {
    if (!enabled) {
      setInFlight(false);
      return;
    }

    const controller = new AbortController();
    let cancelled = false;
    let timer: number | undefined;

    const run = async () => {
      setInFlight(true);
      try {
        const result = await apiFetch<T>(path, {
          signal: controller.signal,
          anonymous,
        });
        if (cancelled) return;
        setData(result);
        setError(null);
        setFetchedAt(new Date());
      } catch (cause) {
        if (cancelled) return;
        if (cause instanceof DOMException && cause.name === 'AbortError') return;
        setError(cause);
        // Deliberately keeps `data`: stale numbers with a visible failure
        // banner beat an empty page during the incident this tool is for.
      } finally {
        if (!cancelled) setInFlight(false);
      }
    };

    void run();

    if (refreshMs && refreshMs > 0) {
      timer = window.setInterval(() => {
        // Polling a backgrounded tab wakes 26 Orthanc probes for nobody. The
        // page re-fetches on focus instead (see below).
        if (document.visibilityState === 'hidden') return;
        void run();
      }, refreshMs);
    }

    const onVisible = () => {
      if (document.visibilityState === 'visible' && refreshMs) void run();
    };
    if (refreshMs) document.addEventListener('visibilitychange', onVisible);

    return () => {
      cancelled = true;
      controller.abort();
      if (timer !== undefined) window.clearInterval(timer);
      if (refreshMs) document.removeEventListener('visibilitychange', onVisible);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, refreshMs, enabled, anonymous, attempt]);

  return {
    data,
    error,
    loading: inFlight && !hasData,
    refreshing: inFlight && hasData,
    fetchedAt,
    reload,
  };
}
